import { format } from 'date-fns'
import { Check, Clock, Dumbbell, Tag } from 'lucide-react'
import React from 'react'
import { useRemoveWorkoutTag, useSetWorkoutTag, useUserSettings } from '../api/hooks/useSettings'
import { cn } from '../lib/utils'
import type { Workout } from '../types'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card } from './ui/card'

interface WorkoutCardProps {
  workout: Workout
  onClick: () => void
}

const WORKOUT_TAGS = [
  {
    value: 'great-pump',
    label: 'Great Pump',
    className: 'bg-orange-500 text-white hover:bg-orange-600'
  },
  { value: 'tired', label: 'Tired', className: 'bg-yellow-500 text-white hover:bg-yellow-600' },
  { value: 'pr-day', label: 'PR Day', className: 'bg-green-500 text-white hover:bg-green-600' },
  { value: 'recovery', label: 'Recovery', className: 'bg-blue-500 text-white hover:bg-blue-600' },
  { value: 'intense', label: 'Intense', className: 'bg-red-500 text-white hover:bg-red-600' },
  { value: 'easy', label: 'Easy', className: 'bg-gray-500 text-white hover:bg-gray-600' },
  { value: 'focused', label: 'Focused', className: 'bg-purple-500 text-white hover:bg-purple-600' }
]

export default function WorkoutCard({ workout, onClick }: WorkoutCardProps) {
  const [showTagMenu, setShowTagMenu] = React.useState(false)
  const tagMenuRef = React.useRef<HTMLDivElement>(null)

  // Use React Query hooks for workout tags
  const { data: userSettings } = useUserSettings()
  const setWorkoutTagMutation = useSetWorkoutTag()
  const removeWorkoutTagMutation = useRemoveWorkoutTag()

  const workoutTags = userSettings?.workoutTags || {}

  const setWorkoutTag = (workoutId: string, tag: string) => {
    setWorkoutTagMutation.mutate({ workoutId, tag })
    setShowTagMenu(false)
  }

  const removeWorkoutTag = (workoutId: string) => {
    removeWorkoutTagMutation.mutate(workoutId)
    setShowTagMenu(false)
  }

  const currentTag = workoutTags?.[workout.id]
  const tagInfo = WORKOUT_TAGS.find((t) => t.value === currentTag)

  // Calculate workout duration
  const duration =
    workout.end_time && workout.start_time
      ? Math.round(
          (new Date(workout.end_time).getTime() - new Date(workout.start_time).getTime()) / 60000
        )
      : null

  // Count total sets
  const totalSets =
    workout.exercises?.reduce((acc, exercise) => acc + (exercise.sets?.length || 0), 0) || 0

  // Get unique exercise count
  const exerciseCount = workout.exercises?.length || 0

  const handleTagChange = (value: string) => {
    if (value === 'none') {
      removeWorkoutTag(workout.id)
    } else {
      setWorkoutTag(workout.id, value)
    }
  }

  // Close tag menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagMenuRef.current && !tagMenuRef.current.contains(event.target as Node)) {
        setShowTagMenu(false)
      }
    }

    if (showTagMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTagMenu])

  // Handle escape key
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowTagMenu(false)
      }
    }

    if (showTagMenu) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showTagMenu])

  return (
    <Card
      className="relative cursor-pointer py-0 transition-colors hover:bg-white/10"
      onClick={onClick}
    >
      <div className="p-4">
        <div className="md:justify- flex flex-col items-start justify-start md:flex-row md:justify-between">
          {/* Left side - workout info */}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-base font-semibold">
                  {workout.title || 'Workout'}
                </span>
                <span className="text-muted-foreground text-sm">
                  {format(new Date(workout.start_time), 'MMM d')} •{' '}
                  {format(new Date(workout.start_time), 'h:mm a')}
                </span>
              </div>

              {/* Exercise preview with badges */}
              <div className="flex items-center gap-2">
                <div className="flex flex-wrap gap-1">
                  {workout.exercises && workout.exercises.length > 0 ? (
                    <>
                      {workout.exercises.slice(0, 4).map((exercise, idx) => (
                        <span key={idx} className="text-muted-foreground text-xs">
                          {exercise.title}
                          {idx < Math.min(3, workout.exercises.length - 1) && ','}
                        </span>
                      ))}
                      {workout.exercises.length > 4 && (
                        <span className="text-muted-foreground text-xs">
                          +{workout.exercises.length - 4} more
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground text-xs italic">
                      No exercises recorded
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right side - stats and actions */}
          <div className="flex flex-shrink-0 items-center gap-3 max-md:mt-4 md:ml-4">
            {/* Stats */}
            <div className="flex items-center gap-3 text-sm">
              <div className="text-right">
                <div className="text-muted-foreground flex items-center gap-1">
                  <Dumbbell className="h-3.5 w-3.5" />
                  <span>{exerciseCount}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-muted-foreground">
                  <span className="text-foreground font-medium">{totalSets}</span>
                  <span className="ml-1">sets</span>
                </div>
              </div>
              {duration && (
                <div className="text-right">
                  <div className="text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{duration}m</span>
                  </div>
                </div>
              )}
            </div>

            {/* Tag section with custom dropdown */}
            <div className="flex items-center gap-2">
              {tagInfo && <Badge className={`text-xs ${tagInfo.className}`}>{tagInfo.label}</Badge>}
              <div className="relative" ref={tagMenuRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowTagMenu(!showTagMenu)
                  }}
                  className="h-8 w-8 cursor-pointer p-0"
                  aria-haspopup="listbox"
                  aria-expanded={showTagMenu}
                >
                  <Tag className="h-4 w-4" />
                </Button>

                {showTagMenu && (
                  <div
                    className="bg-popover animate-in fade-in-0 zoom-in-95 absolute top-full right-0 z-10 mt-1 w-48 rounded-md border p-1 shadow-md"
                    role="listbox"
                    aria-label="Select a tag"
                  >
                    <div className="p-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTagChange('none')
                          setShowTagMenu(false)
                        }}
                        className={cn(
                          'flex w-full items-center justify-between px-2 py-1.5',
                          'rounded-sm text-left text-sm',
                          'transition-colors duration-150',
                          'hover:bg-accent hover:text-accent-foreground',
                          'cursor-pointer',
                          !currentTag && 'bg-accent text-accent-foreground'
                        )}
                      >
                        <span className="text-muted-foreground">No tag</span>
                        {!currentTag && (
                          <Check className="text-primary ml-2 h-4 w-4 flex-shrink-0" />
                        )}
                      </button>
                      {WORKOUT_TAGS.map((tag) => (
                        <button
                          key={tag.value}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleTagChange(tag.value)
                            setShowTagMenu(false)
                          }}
                          className={cn(
                            'flex w-full items-center justify-between px-2 py-1.5',
                            'rounded-sm text-left text-sm',
                            'transition-colors duration-150',
                            'hover:bg-accent hover:text-accent-foreground',
                            'cursor-pointer',
                            currentTag === tag.value && 'bg-accent text-accent-foreground'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`h-3 w-3 rounded-full ${tag.className.split(' ')[0]}`}
                            />
                            <span>{tag.label}</span>
                          </div>
                          {currentTag === tag.value && (
                            <Check className="text-primary ml-2 h-4 w-4 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
